import { useState, useEffect } from "react";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { WEEKDAYS } from "@/lib/calendarUtils";
import { FIELD_JOB_TYPES, JOB_TYPE_LABELS } from "@/lib/fieldTypes";
import { WorkerSelect } from "@/components/WorkerSelect";
import { SearchSelect } from "@/components/SearchSelect";
import { hasField, hasConnect } from "@/lib/mergeStaff";
import { NeverEnds } from "@/components/schedule/NeverEnds";
import { ChecklistPicker } from "@/components/forms/ChecklistPicker";
import { PropertyAddressLines } from "@/components/schedule/PropertyAddressLines";

const UNASSIGNED = "__unassigned__";

export default function EventCreateDialog({ open, onOpenChange, workers, sites, properties, clients = [], admins = [], jobTypes = [], currentUserId, prefill, defaultDate, defaultSlot, can, onCreated, onCreateSite, onCreateProperty, onCreatePropertyFromAddress, onCreateClient }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const canShift = can("schedule.edit");
  const canJob = can("job.manage");
  const [kind, setKind] = useState(canJob ? "job" : "shift");
  const emptyForm = {
    workerId: UNASSIGNED, workerIds: [], siteId: "", clientId: "", propertyId: "",
    jobType: "one_off", jobTypeOther: "", salesmanId: currentUserId || "", addressDetails: "",
    date: defaultDate, startTime: "09:00", endTime: "12:00", notes: "", instructions: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [repeat, setRepeat] = useState(false);
  const [rec, setRec] = useState({ freq: "weekly", interval: 1, byWeekday: [], count: 4, until: "" });
  const [termMode, setTermMode] = useState("term");
  const selectedSite = sites.find((s) => s.id === form.siteId);
  const hasTerm = kind === "shift" && !!selectedSite?.termCalendarId;
  const [followTerm, setFollowTerm] = useState(true);
  const [termInfo, setTermInfo] = useState(null);
  const useTerm = hasTerm && termMode !== "ignore" && followTerm;

  useEffect(() => {
    if (!hasTerm) { setTermInfo(null); return; }
    client.get(`/term-calendars/${selectedSite.termCalendarId}`).then(({ data }) => setTermInfo(data)).catch(() => setTermInfo(null));
  }, [hasTerm, selectedSite?.termCalendarId]);
  const termEnd = termInfo && (termInfo.rangeEnd || [...(termInfo.closures || []).map((c) => c.end), ...(termInfo.specialDays || []).map((s) => s.date)].filter(Boolean).sort().pop());

  const fieldWorkers = workers.filter((w) => hasField(w.operationalAccess || "CONNECT_ONLY"));
  const connectWorkers = workers.filter((w) => hasConnect(w.operationalAccess || "CONNECT_ONLY"));
  const [addrOverride, setAddrOverride] = useState({});
  const propsForClient = properties.filter((p) => !form.clientId || p.clientId === form.clientId)
    .map((p) => (addrOverride[p.id] ? { ...p, address: addrOverride[p.id] } : p));
  const chosenProperty = propsForClient.find((p) => p.id === form.propertyId);
  const jtOptions = jobTypes.length ? jobTypes : FIELD_JOB_TYPES.map((k) => ({ value: k, label: JOB_TYPE_LABELS[k] || k }));
  const [checklists, setChecklists] = useState([]);
  const [picked, setPicked] = useState(null);
  const [checklistRequired, setChecklistRequired] = useState(false);
  useEffect(() => {
    if (open && kind === "job") { client.get("/forms/published").then((r) => setChecklists(r.data || [])).catch(() => setChecklists([])); }
  }, [open, kind]);
  const defaultPicked = checklists.filter((c) => c.jobTypes.includes(form.jobType)).map((c) => c.id);
  const chosen = picked ?? defaultPicked;

  useEffect(() => {
    if (!open) return;
    if (prefill) {
      setKind("job");
      setForm({
        ...emptyForm,
        clientId: prefill.clientId || "", propertyId: prefill.propertyId || "",
        workerIds: prefill.workerIds || [], salesmanId: prefill.salesmanId || currentUserId || "",
        jobType: prefill.jobType || "one_off", jobTypeOther: prefill.jobTypeOther || "", addressDetails: prefill.addressDetails || "",
        date: prefill.date || defaultDate, startTime: prefill.startTime || "09:00", endTime: prefill.endTime || "12:00",
        instructions: prefill.instructions || "", notes: prefill.notes || "",
      });
      return;
    }
    setForm((f) => ({
      ...f,
      date: defaultSlot?.date || defaultDate,
      startTime: defaultSlot?.startTime || f.startTime,
      endTime: defaultSlot?.endTime || f.endTime,
      workerIds: defaultSlot?.workerId ? [defaultSlot.workerId] : f.workerIds,
      salesmanId: f.salesmanId || currentUserId || "",
    }));
  }, [open, defaultSlot, defaultDate, prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => { setForm(emptyForm); setPicked(null); setChecklistRequired(false); setRepeat(false); setRec({ freq: "weekly", interval: 1, byWeekday: [], count: 4, until: "" }); setTermMode("term"); setFollowTerm(true); };

  const toggleWd = (d) => setRec((r) => ({ ...r, byWeekday: r.byWeekday.includes(d) ? r.byWeekday.filter((x) => x !== d) : [...r.byWeekday, d] }));

  const createSiteInline = async (name) => { const id = onCreateSite ? await onCreateSite(name) : null; if (id) setForm((f) => ({ ...f, siteId: id })); };
  const createClientInline = async (name) => { const id = onCreateClient ? await onCreateClient(name) : null; if (id) setForm((f) => ({ ...f, clientId: id, propertyId: "" })); };
  const createPropertyInline = async (address) => { const id = onCreateProperty ? await onCreateProperty(address, form.clientId) : null; if (id) setForm((f) => ({ ...f, propertyId: id })); };
  const createPropertyFromAddress = async (r) => { const id = onCreatePropertyFromAddress ? await onCreatePropertyFromAddress(r, form.clientId) : null; if (id) setForm((f) => ({ ...f, propertyId: id })); };

  const toggleWorker = (id) => setForm((f) => ({ ...f, workerIds: f.workerIds.includes(id) ? f.workerIds.filter((x) => x !== id) : [...f.workerIds, id] }));

  const submit = async () => {
    setBusy(true);
    try {
      const body = { kind, date: form.date, startTime: form.startTime, endTime: form.endTime, notes: form.notes };
      if (kind === "shift") {
        body.siteId = form.siteId;
        body.workerIds = form.workerIds;
        body.workerId = form.workerIds[0] || null;
        if (hasTerm) body.termMode = termMode;
        if (useTerm) body.followTerm = true;
      } else {
        body.clientId = form.clientId; body.propertyId = form.propertyId;
        body.jobType = form.jobType; body.jobTypeOther = form.jobTypeOther; body.addressDetails = form.addressDetails;
        body.instructions = form.instructions; body.workerIds = form.workerIds;
        body.workerId = form.workerIds[0] || null; body.salesmanId = form.salesmanId || null;
        body.checklistTemplateIds = chosen;
        body.checklistRequired = chosen.length > 0 && checklistRequired;
      }
      if (repeat && !useTerm) {
        body.recurrence = {
          freq: rec.freq, interval: Number(rec.interval) || 1,
          byWeekday: rec.freq === "weekly" ? rec.byWeekday : [],
          count: rec.forever || rec.until ? null : Number(rec.count) || 1, until: rec.forever ? null : rec.until || null,
          forever: !!rec.forever,
        };
      }
      const { data } = await client.post("/calendar/event", body);
      const extra = data.skippedClosures ? ` · ${data.skippedClosures} skipped (term closures)` : "";
      toast.success(`${t("toast.eventCreated", "Created")} (${data.count})${extra}`);
      reset(); onOpenChange(false); onCreated();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const valid = form.date && (kind === "shift" ? form.siteId : form.propertyId);
  const [nextNo, setNextNo] = useState(null);
  useEffect(() => {
    if (open && kind === "job") {
      client.get("/calendar/next-job-number").then(({ data }) => setNextNo(data.jobNumber)).catch(() => setNextNo(null));
    }
  }, [open, kind]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="event-create-dialog" className="block max-w-none w-screen h-[100dvh] max-h-[100dvh] sm:rounded-none overflow-y-auto p-0 border-0">
       <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-10 sm:py-10 space-y-6">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-3 pr-8">
            <DialogTitle className="text-2xl sm:text-3xl">{prefill ? t("cal.duplicate", "Duplicate job") : kind === "job" ? t("cal.newJob", "New job") : t("cal.newShift", "New shift")}</DialogTitle>
            {kind === "job" && nextNo != null && (
              <span data-testid="create-next-job-number" className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-1 font-mono text-xl font-semibold text-brand">#{nextNo}</span>
            )}
          </div>
          <DialogDescription className="text-base">{kind === "job" && nextNo != null ? t("cal.nextJobHint", "This job will be saved as job #{n}.").replace("{n}", nextNo) : t("cal.subtitle", "Shifts and jobs across your sites, workers and properties.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 text-base [&_label]:text-base [&_label]:font-semibold [&_input]:h-12 [&_input]:text-base [&_button[role=combobox]]:h-12 [&_button[role=combobox]]:text-base [&_button[aria-haspopup=dialog]]:h-12 [&_button[aria-haspopup=dialog]]:text-base [&_textarea]:text-base [&_textarea]:min-h-[96px]">
          {canShift && canJob && !prefill && (
            <div className="flex gap-2" data-testid="event-kind-toggle">
              <Button type="button" variant={kind === "shift" ? "default" : "outline"}
                data-testid="kind-shift-btn" className={`h-11 px-6 text-base ${kind === "shift" ? "bg-teal-600 hover:bg-teal-700" : ""}`} onClick={() => setKind("shift")}>
                {t("cal.shift", "Shift")}
              </Button>
              <Button type="button" variant={kind === "job" ? "default" : "outline"}
                data-testid="kind-job-btn" className={`h-11 px-6 text-base ${kind === "job" ? "bg-brand hover:bg-brand-dark" : ""}`} onClick={() => setKind("job")}>
                {t("cal.job", "Job")}
              </Button>
            </div>
          )}

          {kind === "shift" ? (
            <div>
              <Label>{t("cal.site", "Site")}</Label>
              <SearchSelect testid="event-site-select" value={form.siteId} onChange={(v) => {
                  const site = sites.find((s) => s.id === v);
                  const auto = (site?.assignedCleanerIds || []).filter((id) => connectWorkers.some((w) => w.id === id));
                  setForm({ ...form, siteId: v, workerIds: auto });
                }}
                options={sites.map((s) => ({ value: s.id, label: s.name }))}
                placeholder={t("cal.site", "Site")} searchPlaceholder={t("cal.searchSite", "Search or add a site…")}
                onCreate={onCreateSite ? createSiteInline : undefined} createLabel={t("sites.new", "Create site")} className="mt-1.5" />
              {hasTerm && (
                <div className="mt-2 rounded-lg border border-teal-200 bg-teal-50/50 p-2.5 space-y-1.5" data-testid="event-term-mode-box">
                  <p className="text-[11px] font-semibold text-teal-700">This site follows a term calendar</p>
                  <Select value={termMode} onValueChange={setTermMode}>
                    <SelectTrigger data-testid="event-term-mode" className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="term">Term-time only (skip holidays)</SelectItem>
                      <SelectItem value="holiday">Holiday work (only during closures)</SelectItem>
                      <SelectItem value="ignore">Ignore calendar (every date)</SelectItem>
                    </SelectContent>
                  </Select>
                  {termMode !== "ignore" && (
                    <div className="flex items-center justify-between pt-1">
                      <div>
                        <p className="text-xs font-semibold text-slate-700">Follow school schedule</p>
                        <p className="text-[11px] text-slate-500" data-testid="event-term-range">Every school day from the date below{termEnd ? ` until ${termEnd}` : ""}</p>
                      </div>
                      <Switch checked={followTerm} onCheckedChange={setFollowTerm} data-testid="event-follow-term-switch" />
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4">
                <Label>{t("cal.cleaners", "Cleaners")}</Label>
                <WorkerSelect workers={connectWorkers} value={form.workerIds} onChange={(ids) => setForm({ ...form, workerIds: ids })} testid="event-shift-workers" placeholder={t("cal.selectCleaners", "Select cleaners…")} />
                <p className="text-[11px] text-slate-400 mt-1">{t("cal.autoCleaners", "Auto-filled from the site's assigned cleaners — add or remove as needed.")}</p>
                {(() => {
                  const blocked = (selectedSite?.assignedCleanerIds || []).map((id) => workers.find((w) => w.id === id)).filter((w) => w && !hasConnect(w.operationalAccess || "CONNECT_ONLY"));
                  return blocked.length > 0 && (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-1.5" data-testid="event-field-only-warning">
                      {blocked.map((w) => w.displayName).join(", ")} {blocked.length === 1 ? "is" : "are"} assigned to this site but set to Field only, so can't be put on shifts. Change their access to Connect + Field in Staff to include them.
                    </p>
                  );
                })()}
              </div>
            </div>
          ) : (
            <>
              <div>
                <Label>{t("cal.client", "Client")}</Label>
                <SearchSelect testid="event-client-select" value={form.clientId} onChange={(v) => setForm({ ...form, clientId: v, propertyId: "" })}
                  options={clients.map((c) => ({ value: c.id, label: c.name }))}
                  placeholder={t("cal.client", "Client")} searchPlaceholder={t("job.searchClient", "Search or add a client…")}
                  onCreate={onCreateClient ? createClientInline : undefined} createLabel={t("client.new", "Create client")} className="mt-1.5" />
              </div>
              <div>
                <Label>{t("cal.property", "Property")}</Label>
                <SearchSelect testid="event-property-select" value={form.propertyId} onChange={(v) => setForm({ ...form, propertyId: v })}
                  options={propsForClient.map((p) => ({ value: p.id, label: p.address }))}
                  placeholder={form.clientId ? t("cal.property", "Property") : t("job.pickClientFirst", "Pick a client first")}
                  searchPlaceholder={t("job.searchProperty", "Search or add an address…")}
                  onCreate={onCreateProperty ? createPropertyInline : undefined}
                  onCreateFromAddress={onCreatePropertyFromAddress ? createPropertyFromAddress : undefined}
                  addressSuggest={!!onCreatePropertyFromAddress} createLabel={t("prop.new", "Create property")} className="mt-1.5" />
                {chosenProperty && (
                  <div className="mt-3">
                    <PropertyAddressLines property={chosenProperty} onSaved={(a) => setAddrOverride((o) => ({ ...o, [chosenProperty.id]: a }))} />
                  </div>
                )}
              </div>
              <div>
                <Label>{t("job.addressDetails", "Extra address details (flat, unit, floor)")}</Label>
                <Input data-testid="event-address-details" value={form.addressDetails} onChange={(e) => setForm({ ...form, addressDetails: e.target.value })}
                  placeholder={t("job.addressDetailsPlaceholder", "e.g. Flat 3, 2nd floor, buzzer 12")} className="mt-1.5" />
              </div>
              <div>
                <Label>{t("job.type", "Job type")}</Label>
                <Select value={form.jobType} onValueChange={(v) => setForm({ ...form, jobType: v })}>
                  <SelectTrigger data-testid="event-jobtype-select" className="mt-1.5 h-12 text-base"><SelectValue /></SelectTrigger>
                  <SelectContent>{jtOptions.map((jt) => <SelectItem key={jt.value} value={jt.value}>{jt.label}</SelectItem>)}</SelectContent>
                </Select>
                {form.jobType === "other" && (
                  <Input data-testid="event-jobtype-other" value={form.jobTypeOther} onChange={(e) => setForm({ ...form, jobTypeOther: e.target.value })} placeholder={t("job.typeOther", "Describe (other)")} className="mt-2" />
                )}
              </div>
              <div>
                <Label>{t("job.fieldWorkers", "Field workers")}</Label>
                <WorkerSelect workers={fieldWorkers} value={form.workerIds} onChange={(ids) => setForm({ ...form, workerIds: ids })} testid="event-field-workers" placeholder={t("job.selectWorkers", "Select workers…")} />
              </div>
              <div>
                <Label>{t("job.connectWorkers", "Connect workers")}</Label>
                <WorkerSelect workers={connectWorkers} value={form.workerIds} onChange={(ids) => setForm({ ...form, workerIds: ids })} testid="event-connect-workers" placeholder={t("job.selectWorkers", "Select workers…")} />
                <p className="text-sm text-slate-500 mt-1.5">{t("job.multiHint", "Assign one or more workers. Each records their own time.")}</p>
              </div>
              <div>
                <Label>{t("job.checklists", "Checklists")}</Label>
                <p className="text-sm text-slate-500 mb-2">{t("job.checklistsHint", "Ticked automatically for this job type. Tap to add or remove.")}</p>
                <ChecklistPicker options={checklists} value={chosen} onChange={setPicked} testid="event-checklists" />
                {chosen.length > 0 && (
                  <label className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer">
                    <span><span className="block text-sm font-medium text-slate-800">{t("job.checklistRequired", "Must be completed before the job can be finished")}</span>
                      <span className="block text-xs text-slate-500">{t("job.checklistRequiredHint", "Cleaners can't end this job in the app until its checklists are submitted.")}</span></span>
                    <Switch checked={checklistRequired} onCheckedChange={setChecklistRequired} data-testid="event-checklist-required" />
                  </label>
                )}
              </div>
              <div>
                <Label>{t("job.salesman", "Salesman (added by)")}</Label>
                <Select value={form.salesmanId} onValueChange={(v) => setForm({ ...form, salesmanId: v })}>
                  <SelectTrigger data-testid="event-salesman-select" className="mt-1.5 h-12 text-base"><SelectValue placeholder={t("job.selectSalesman", "Select salesman")} /></SelectTrigger>
                  <SelectContent>{admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.displayName}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="min-w-0"><Label>{useTerm ? "Starts on" : t("cal.date", "Date")}</Label><Input type="date" data-testid="event-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 w-full min-w-0 appearance-none px-2" /></div>
            <div className="min-w-0"><Label>{t("cal.start", "Start")}</Label><Input type="time" data-testid="event-start-input" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className="mt-1.5 w-full min-w-0 appearance-none px-2" /></div>
            <div className="min-w-0"><Label>{t("cal.end", "End")}</Label><Input type="time" data-testid="event-end-input" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className="mt-1.5 w-full min-w-0 appearance-none px-2" /></div>
          </div>

          {kind === "job" && (
            <div><Label>{t("cal.instructions", "Instructions")}</Label><Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="mt-1.5" /></div>
          )}
          <div><Label>{t("cal.notes", "Notes")}</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>

          {!useTerm && (
          <div className="flex items-center justify-between border-t border-slate-100 pt-3">
            <Label className="cursor-pointer">{t("cal.repeat", "Repeat")}</Label>
            <Switch checked={repeat} onCheckedChange={setRepeat} data-testid="event-repeat-switch" />
          </div>
          )}

          {repeat && !useTerm && (
            <div className="space-y-3 rounded-lg bg-slate-50 p-3" data-testid="recurrence-panel">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("cal.freq", "Frequency")}</Label>
                  <Select value={rec.freq} onValueChange={(v) => setRec({ ...rec, freq: v })}>
                    <SelectTrigger data-testid="recur-freq-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">{t("cal.daily", "Daily")}</SelectItem>
                      <SelectItem value="weekly">{t("cal.weekly", "Weekly")}</SelectItem>
                      <SelectItem value="monthly">{t("cal.monthly", "Monthly")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("cal.interval", "Every")}</Label><Input type="number" min="1" value={rec.interval} onChange={(e) => setRec({ ...rec, interval: e.target.value })} className="mt-1.5" /></div>
              </div>
              {rec.freq === "weekly" && (
                <div>
                  <Label>{t("cal.weekdays", "On days")}</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {WEEKDAYS.map((d) => (
                      <button key={d} type="button" data-testid={`recur-wd-${d}`} onClick={() => toggleWd(d)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${rec.byWeekday.includes(d) ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
                        {t(`wd.${d}`, ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][d])}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <NeverEnds rec={rec} setRec={setRec} testid="event-recur-forever" />
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("cal.endsAfter", "Ends after")}</Label><Input type="number" min="1" value={rec.count} disabled={!!rec.until || rec.forever} onChange={(e) => setRec({ ...rec, count: e.target.value })} className="mt-1.5" placeholder={t("cal.occurrences", "occurrences")} /></div>
                <div><Label>{t("cal.endsOn", "Ends on")}</Label><Input type="date" value={rec.until} disabled={rec.forever} onChange={(e) => setRec({ ...rec, until: e.target.value })} className="mt-1.5" /></div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="sticky bottom-0 bg-background border-t pt-4 pb-4 flex-row gap-3">
          <Button variant="outline" size="lg" className="flex-1 h-12 text-base" data-testid="event-cancel-btn" onClick={() => { reset(); onOpenChange(false); }}>{t("common.cancel", "Cancel")}</Button>
          <Button data-testid="event-save-btn" size="lg" onClick={submit} disabled={busy || !valid} className="flex-1 h-12 text-base bg-brand hover:bg-brand-dark">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.create", "Create")}
          </Button>
        </DialogFooter>
       </div>
      </DialogContent>
    </Dialog>
  );
}
